import { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { DataGridColumn, LookupDetails, VigoDataGridProps } from './datagrid-types';
import CustomChip from 'src/@core/components/mui/chip';
import Link from 'next/link';
import { format } from 'date-fns';
import {
  DataGridPremium as MUIDataGrid,
  DataGridPremiumProps,
  GridActionsCellItem,
  GridColDef,
  GridEditInputCell,
  GridRenderCellParams,
  GridRenderEditCellParams,
  GridRowModes,
  GridRowModesModel,
  GridRowsProp,
  GridRowSelectionModel,
  GridRowModel,
  GridSortModel
} from '@mui/x-data-grid-premium';
import { defaultColumns } from './defaults';
import { darken, lighten } from '@mui/material/styles';
import { useVigoTranslation } from 'src/@core/hooks/useVigoTranslation';
import VigoSpinner from 'src/@vigo/vigo-spinner';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import { LookupInformationDto } from 'src/models/systemconfig';
import VigoAutoComplete from '../vigo-autocomplete';
import { VigoCheckBox } from '../vigo-checkbox';
import { VigoTextInput } from '../vigo-textinput';
import { GridCallbackDetails, useGridApiRef } from '@mui/x-data-grid-premium';
import { styled } from '@mui/material/styles';
import React from 'react';
import { useAuth } from 'react-oidc-context';
import { useTheme } from '@mui/material';
import { useVigoCamelCase } from 'src/@core/hooks/useVigoCamelCase';
import {
  buildNonEditableCustomActions,
  convertToDataTableType,
  handleCancelClick,
  handleChipColor,
  handleChipText,
  handleCustomChipColor,
  handleEditClick,
  handleImportRowChange,
  handleRowEditStart,
  handleRowEditStop,
  handleSaveClick,
  mapEditableRows
} from './helpers';
import { usePersistGridState } from './usePersistGridState';
import { useRowSelectionAcrossSearch } from './useRowSelectionAcrossSearch';
import { useIsFetching } from '@tanstack/react-query';
import { useAppDispatch } from 'src/store/baseHooks';
import { setSelectedEntityId } from 'src/store/slices/attachmentsDataSlice';
import { useGridApiRefContext } from 'src/@core/context/gridRefContext';
import CustomToggle from './components/CustomToggle';
import CustomToolbar from './components/CustomToolbar';
import CustomFooter from './components/CustomFooter';

const FilterPanel = styled('div')(({ theme }) => ({
  height: '80px',
  margin: '0px',
  boxShadow: 'none',
  borderRadius: '0px',
  padding: '0px',
  scrollbars: 'none',
  overflow: 'none',
  whitespace: 'nowrap',
  backgroundColor: theme.palette.background.default
}));

export const VigoDataGrid = ({
  onCellClick = () => {
    return;
  },
  name,
  dataSource,
  totalRows,
  currentLoadedRows,
  editServiceEndpoint,
  editServiceLocation,
  lookupColumns,
  masterDataObject,
  fetchMoreRowsPageSize,
  selectedTableId,
  refresh,
  filterColumn,
  columns,
  disableColumnMenu,
  disableSorting,
  customActions = [],
  defaultActions,
  multiSelect,
  setSelectedRowIds,
  multiSelectButtons,
  density = 'compact',
  disableImport,
  disableExport,
  disablePrint,
  headerButtonGroup,
  initialState,
  disableSearch,
  disableSelectionOnClick = false,
  columnVisibilityModel,
  isEditable,
  isRowSelectable,
  editMode,
  autoHeight = false,
  experimentalFeatures,
  setRowsFromCSV,
  editableColumns,
  setRowsToCheck,
  onStateChange,
  sx,
  sort = '',
  filterCollection,
  onRowClick = () => {
    return;
  },
  onFilterItemSelected = () => {
    return;
  },
  filterDateRange,
  wrapHeader,
  defaultGridState,
  hideActions,
  disableLayoutReset = false,
  disableVirtualization = true,
  filterModel,
  fullDataSource,
  setLookupColumns,
  setMasterDataRows,
  onScrollEnd,
  tenantId,
  partnerId,
  setCustomType,
  sortingMode,
  handleServerSort,
  gridLoading,
  hideFooter,
  usePagination,
  searchOverride,
  disableRowSelectionOnClick,
  keepNonExistentRowsSelected = false,
  customPlaceholder,
  rowSelectionModel,
  queryKey,
  disableAggregation,
  slots,
  slotProps,
  rowReordering,
  onRowOrderChange,
  initialSearchValue,
  searchRef
}: VigoDataGridProps) => {
  const [gridRows, setGridRows] = useState<any[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [totalRowSize, setTotalRowSize] = useState<number>(0);
  const [currentLoadedRowsSize, setCurrentLoadedRowsSize] = useState<number>(0);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [showMultiSelectButtons, setShowMultiSelectButtons] = useState<boolean>(false);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [editableGridRows, setEditableGridRows] = useState<GridRowsProp>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  const apiRef = useGridApiRef();
  const auth = useAuth();
  const theme = useTheme();
  const { t } = useVigoTranslation();
  const uiFlowDispatch = useAppDispatch();
  const { toCamelCase } = useVigoCamelCase();
  const { dispatch: gridRefDispatch } = useGridApiRefContext();

  const fetchPageSize = fetchMoreRowsPageSize ? fetchMoreRowsPageSize : 25;
  const user = auth.user;
  const loggedInUserId = user?.profile.tenantUserId as string;

  const isFetching = useIsFetching(queryKey ? [queryKey] : undefined);
  const storageKey = `gridState-${name?.toLowerCase()}-${loggedInUserId}`;

  const savedState = localStorage.getItem(storageKey) ?? defaultGridState;

  const { resetLayout } = usePersistGridState(
    apiRef,
    storageKey,
    typeof savedState === 'string' ? JSON.parse(savedState ?? {}) : savedState,
    defaultGridState
  );

  const { onSearchChange } = useRowSelectionAcrossSearch(apiRef, gridRows, multiSelect);

  const localeText = useMemo(
    () => ({
      footerRowSelected: (count: number) =>
        `${count.toLocaleString()} ${count === 1 ? t('row selected') : t('rows selected')}`
    }),
    [t]
  );

  const getInitialState = useMemo(() => {
    return {
      ...(initialState ?? {}),
      columns: {
        ...(initialState?.columns ?? {}),
        columnVisibilityModel: columnVisibilityModel ?? initialState?.columns?.columnVisibilityModel ?? {}
      }
    };
  }, [columnVisibilityModel, initialState]);

  const processRowUpdate = (newRow: GridRowModel) => {
    const updatedRow = { ...newRow };
    setEditableGridRows(editableGridRows.map(row => (row.id === newRow.id ? updatedRow : row)));

    const rowToUpdate = dataSource.findIndex(x => x.id == updatedRow.id);
    dataSource[rowToUpdate] = updatedRow;

    if (fullDataSource) {
      fullDataSource[rowToUpdate] = updatedRow;
    }

    setRowsFromCSV?.(fullDataSource ?? []);
    setRowsToCheck?.(dataSource);

    return updatedRow;
  };

  const GridSpinner = () => {
    return <VigoSpinner customSx={{ color: 'red', height: '50vh' }} name="datagridspinner" />;
  };

  const buildColumn = useCallback(
    (col: DataGridColumn): DataGridColumn => {
      const isTime = col.type === 'time';
      if (isTime) {
        col.type = 'string';
      }

      const isETA = col.type === 'ETA';
      if (isETA) {
        col.type = 'string';
      }

      const isToggle = col.type === 'toggle';
      if (isToggle) {
        col.type = 'boolean';
      }

      // Add column dimensions and other properties from saved grid state
      try {
        const stateCol = apiRef.current?.getAllColumns()?.find(c => c.field === col.field);
        if (stateCol && col.field !== 'actions') {
          col.width = stateCol.width;
        }
      } catch (error) {
        // ignore
      }

      return {
        ...col,
        ...(col.type === 'number' && { align: col.align ?? 'left', headerAlign: col.headerAlign ?? 'left' }),
        ...(disableSorting && { sortable: false }),
        ...(col.type === 'chip' && {
          type: 'string',
          renderCell: ({ row }) => (
            <CustomChip
              skin="light"
              size={col.chipOptions?.size}
              label={handleChipText(col, row)}
              color={handleChipColor(col, row)}
              sx={{
                textTransform: 'capitalize',
                ...(col?.chipOptions?.customColorOverride && { backgroundColor: handleCustomChipColor(col, row) })
              }}
            />
          )
        }),
        ...(((col.type !== 'chip' && col.type !== 'boolean' && !col.renderCell) || isToggle) && {
          renderCell: ({ row }: { row: any }) => (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column' }}>
                {col.route && (
                  <Typography
                    noWrap
                    component={Link}
                    href={col.route || '#'}
                    variant="body2"
                    sx={{ ...col.fieldStyles, fontWeight: 600, color: 'text.primary', textDecoration: 'none' }}>
                    {row[col.field]}
                  </Typography>
                )}
                {!col.route && col.type !== 'dateTime' && !isTime && !isETA && (
                  <Typography noWrap component="a" variant="body2" sx={{ ...col.fieldStyles }}>
                    {row[col.field]}
                  </Typography>
                )}
                {col.type === 'dateTime' && row[col.field] && row[col.field] != '' && (
                  <Typography noWrap component="a" variant="body2" sx={{ ...col.fieldStyles }}>
                    {format(new Date(row[col.field]), 'dd/MM/yy')}
                  </Typography>
                )}

                {isTime && row[col.field] && row[col.field] != '' && (
                  <Typography noWrap component="a" variant="body2" sx={{ ...col.fieldStyles }}>
                    {new Date(row[col.field]).toLocaleTimeString().substring(0, 5)}
                  </Typography>
                )}
                {isToggle && (
                  <CustomToggle fieldStyles={col.fieldStyles} updateCallback={col.updateCallback} row={row} col={col} />
                )}
                {isETA && row[col.field] && row[col.field] != '' && (
                  <>
                    <Typography noWrap component="a" variant="body2" sx={{ ...col.fieldStyles }}>
                      {new Date(row[col.field]).toLocaleTimeString().substring(0, 5)}
                    </Typography>
                    <Typography
                      noWrap
                      component="a"
                      variant="caption"
                      sx={{ ...col.fieldLine2Styles, textDecoration: 'none' }}>
                      {format(new Date(row[col.field]), 'dd/MM/yy')}
                    </Typography>
                  </>
                )}
                {col.fieldLine2 && (
                  <Typography
                    noWrap
                    component="a"
                    variant="caption"
                    sx={{ ...col.fieldLine2Styles, textDecoration: 'none' }}>
                    {col.fieldLine2}
                  </Typography>
                )}
              </Box>
            </Box>
          )
        }),
        ...(wrapHeader && {
          renderHeader: () => (
            <div
              style={{
                whiteSpace: 'normal',
                fontWeight: '800',
                lineHeight: '0.9rem',
                padding: '0px',
                wordWrap: 'break-word'
              }}>
              {t(col.headerName)}
            </div>
          )
        }),
        ...(!wrapHeader && {
          renderHeader: () => (
            <div
              style={{
                whiteSpace: 'normal',
                fontWeight: '800',
                lineHeight: '0.9rem',
                padding: '0px',
                wordWrap: 'initial'
              }}>
              {t(col.headerName)}
            </div>
          )
        })
      };
    },
    [apiRef, disableSorting, t, wrapHeader]
  );

  const nonEditableColumns = useMemo(() => {
    const nonEditCustomActions = buildNonEditableCustomActions(customActions);
    const processedCols = [...columns, ...defaultColumns(nonEditCustomActions, defaultActions, hideActions)].map(
      col => {
        return buildColumn(col);
      }
    );

    return processedCols;
  }, [buildColumn, columns, customActions, defaultActions, hideActions]);

  const getEditableDataGridColumns = useCallback(
    (data: any[]): GridColDef[] => {
      if (isEditable) {
        const editableDataGridColumns: GridColDef[] = [];
        for (let index = 0; index < data.length; index++) {
          const column = data[index];

          if (column == 'importOrUpdate') {
            editableDataGridColumns.push({
              field: 'importOrUpdate',
              headerName: 'Import Or Update',
              type: 'string',
              width: 150,
              editable: false
            });

            continue;
          }

          if (column == 'importRow') {
            editableDataGridColumns.push({
              field: 'importRow',
              headerName: 'Import Row?',
              type: 'boolean',
              width: 150,
              editable: false,
              renderCell: (params: GridRenderCellParams) => {
                return (
                  <VigoCheckBox
                    {...params}
                    name={`${column.name}`}
                    value={params.value}
                    onChange={(e, c) => {
                      handleImportRowChange(params, c, dataSource, fullDataSource, setRowsFromCSV, setRowsToCheck);
                    }}
                  />
                );
              }
            });

            continue;
          }

          const dtoProperty = masterDataObject?.dtoProperties?.find(x => x.id == column.dtoPropertyId);

          const lookupData: LookupInformationDto | undefined =
            masterDataObject?.lookupData != undefined
              ? masterDataObject.lookupData?.find(x => x.lookupDtoId == column.lookupDtoId)
              : {};

          const lookupEndpoint = lookupData ? lookupData.lookupEndpoint : '';

          editableDataGridColumns.push({
            field: toCamelCase(column.name),
            headerName: column.name,
            type: convertToDataTableType(
              masterDataObject?.propertyTypes?.find(x => x.id == dtoProperty?.propertyTypeId)?.code
            ),
            width: 150,
            editable: true,
            renderEditCell: (params: GridRenderEditCellParams) => {
              const inputProps = {
                maxLength: column.maxLength,
                minLength: column.minLength
              };

              const field = params.field;
              const row = params.row;

              switch (masterDataObject?.propertyTypes?.find(x => x.id == dtoProperty?.propertyTypeId)?.code) {
                case 'string':
                  return (
                    <VigoTextInput
                      {...params}
                      fullWidth
                      error={row.errorList?.find((x: { field: string }) => x.field == field) != undefined}
                      errormessage={
                        row.errorList?.find((x: { field: string }) => x.field == field) != undefined
                          ? row.errorList?.find((x: { field: string }) => x.field == field).errorMessage
                          : undefined
                      }
                      name={`${column.name} - edit`}
                      value={params.value}
                      useDataGridContext
                      dataGridContextParams={params}
                      inputProps={inputProps}
                    />
                  );

                case 'int':
                  return (
                    <VigoTextInput
                      {...params}
                      fullWidth
                      name={`${column.name} - edit`}
                      value={params.value}
                      type="number"
                      useDataGridContext
                      dataGridContextParams={params}
                      inputProps={inputProps}
                    />
                  );

                case 'uniqueidentifier': {
                  const lookupDetails: LookupDetails | undefined = lookupColumns?.find(
                    x => x.lookupInfo.lookupEndpoint == lookupEndpoint
                  );
                  const lookupDisplayColumn = lookupDetails?.lookupInfo.lookupDtoProperties?.find(
                    x => x.id == lookupDetails.lookupInfo.lookupDisplayPropertyId
                  )?.name;
                  const col = toCamelCase(lookupDisplayColumn);
                  const value = lookupDetails?.lookupRows?.find(x => x[col] == params.value);

                  return (
                    <VigoAutoComplete
                      {...params}
                      fullWidth
                      error={row.errorList?.find((x: { field: string }) => x.field == field) != undefined}
                      errormessage={
                        row.errorList?.find((x: { field: string }) => x.field == field) != undefined
                          ? row.errorList?.find((x: { field: string }) => x.field == field).errorMessage
                          : undefined
                      }
                      displayName={col}
                      required
                      name={`${column.name} - edit`}
                      serviceLocation={lookupEndpoint ? lookupEndpoint?.split('.')[0] : ''}
                      serviceEndpoint={lookupEndpoint ? lookupEndpoint?.split('.')[1] : ''}
                      value={value != undefined ? value.id : undefined}
                      allowAddNewOption={false}
                      useDataGridContext
                      dataGridContextParams={params}
                    />
                  );
                }

                case 'bool':
                  return (
                    <VigoCheckBox
                      {...params}
                      name={`${column.name} - edit`}
                      value={params.value}
                      useDataGridContext
                      dataGridContextParams={params}
                    />
                  );

                default:
                  return <GridEditInputCell {...params} />;
              }
            }
          });
        }

        editableDataGridColumns.push({
          hideable: false,
          field: 'actions',
          type: 'actions',
          headerName: 'Actions',
          width: 100,
          cellClassName: 'actions',
          getActions: ({ id }: any) => {
            const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

            if (isInEditMode) {
              return [
                <GridActionsCellItem
                  key={editableDataGridColumns.length - 2}
                  icon={<SaveIcon />}
                  label={t('Save')}
                  onClick={handleSaveClick(id, setRowModesModel, rowModesModel)}
                />,
                <GridActionsCellItem
                  key={editableDataGridColumns.length - 1}
                  icon={<CancelIcon />}
                  label={t('Cancel')}
                  className="textPrimary"
                  onClick={handleCancelClick(id, setRowModesModel, rowModesModel)}
                  color="inherit"
                />
              ];
            }

            return [
              <GridActionsCellItem
                key={editableDataGridColumns.length - 2}
                icon={<EditIcon />}
                label={t('Edit')}
                className="textPrimary"
                onClick={handleEditClick(id, setRowModesModel, rowModesModel)}
                color="inherit"
              />
            ];
          }
        });

        return editableDataGridColumns;
      }

      return [];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isEditable,
      lookupColumns,
      masterDataObject?.dtoProperties,
      masterDataObject?.lookupData,
      masterDataObject?.propertyTypes,
      rowModesModel
    ]
  );

  const editableDataGridColumns = [...getEditableDataGridColumns(editableColumns ? editableColumns : [])];

  const handleOnRowsScrollEnd: DataGridPremiumProps['onRowsScrollEnd'] = params => {
    if (typeof onScrollEnd === 'function') {
      return onScrollEnd(params, setLoading);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSelectionModelChange = (newSelectionModel: GridRowSelectionModel, details: GridCallbackDetails<any>) => {
    if (newSelectionModel.length === 1) {
      uiFlowDispatch(setSelectedEntityId(newSelectionModel[0].toString()));
    }

    newSelectionModel.length > 0 ? setShowMultiSelectButtons(true) : setShowMultiSelectButtons(false);
    setSelectedRowIds?.(newSelectionModel);
    setSelectionModel(newSelectionModel);
  };

  const handleOnSortModelChange = (model: GridSortModel) => {
    if (JSON.stringify(model) !== JSON.stringify(sortModel)) {
      setSortModel(model);
      handleServerSort?.(model);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  };

  const getBackgroundColor = (color: string, mode: string) =>
    mode === 'dark' ? darken(color, 0.9) : lighten(color, 0.9);

  const getHoverBackgroundColor = (color: string, mode: string) =>
    mode === 'dark' ? darken(color, 0.9) : lighten(color, 0.9);

  useEffect(() => {
    setCurrentLoadedRowsSize(currentLoadedRows);
  }, [currentLoadedRows]);

  useEffect(() => {
    setTotalRowSize(totalRows);
  }, [totalRows]);

  useEffect(() => {
    setLoading(true);
    isEditable
      ? mapEditableRows(dataSource, setRowsFromCSV, fullDataSource, setEditableGridRows)
      : setGridRows(dataSource);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSource]);

  useEffect(() => {
    setLoading(gridLoading ?? false);
  }, [gridLoading]);

  useEffect(() => {
    gridRefDispatch({ type: 'setGridApiRef', payload: apiRef });
  }, [apiRef, gridRefDispatch]);

  // get the value for isFetching to have a nice indicator while loading
  function getLoading(): boolean {
    if (isFetching > 0) {
      return true;
    }

    return false;
  }

  return (
    <>
      {filterCollection && (
        <FilterPanel>
          {React.createElement(filterCollection, { onSelect: onFilterItemSelected, filter: filterDateRange })}
        </FilterPanel>
      )}

      <Box
        sx={
          !isEditable
            ? {
                '& .deleted--true': {
                  bgcolor: theme => getBackgroundColor(theme.palette.error.main, theme.palette.mode),
                  '&:hover': {
                    bgcolor: theme => getHoverBackgroundColor(theme.palette.error.main, theme.palette.mode)
                  }
                },
                backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1e1e1e',
                height: filterCollection != null ? 'calc(100% - 80px)' : '100%',
                width: '100%'
              }
            : {
                '& .editable-grid-theme--true': {
                  bgcolor: theme => getBackgroundColor(theme.palette.error.main, theme.palette.mode),
                  '&:hover': {
                    bgcolor: theme => getHoverBackgroundColor(theme.palette.error.main, theme.palette.mode)
                  },
                  '&.Mui-selected': {
                    bgcolor: theme => getBackgroundColor(theme.palette.error.main, theme.palette.mode),
                    '&:hover': {
                      bgcolor: theme => getHoverBackgroundColor(theme.palette.error.main, theme.palette.mode)
                    }
                  }
                }
              }
        }>
        <MUIDataGrid
          apiRef={apiRef}
          cellSelection
          disableAggregation={disableAggregation}
          disableRowGrouping={true}
          loading={getLoading()}
          onRowsScrollEnd={handleOnRowsScrollEnd}
          getCellClassName={params => `deleted--${params.row.isDeleted}`}
          onStateChange={onStateChange}
          initialState={getInitialState}
          isRowSelectable={isRowSelectable}
          rows={!isEditable ? gridRows : editableGridRows}
          columns={!isEditable ? nonEditableColumns : editableDataGridColumns}
          disableColumnFilter={true}
          checkboxSelection={multiSelect}
          onRowSelectionModelChange={handleSelectionModelChange}
          getRowId={isEditable ? row => row.code : undefined}
          sortingMode={sortingMode}
          onSortModelChange={model => handleOnSortModelChange(model)}
          sortModel={sortModel}
          hideFooter={hideFooter}
          localeText={localeText}
          keepNonExistentRowsSelected={keepNonExistentRowsSelected || multiSelect}
          slots={{
            toolbar: CustomToolbar,
            footer: CustomFooter,
            loadingOverlay: GridSpinner,
            ...slots
          }}
          slotProps={{
            toolbar: {
              disableImport,
              disableExport,
              disableFilters: true,
              disableSearch,
              disablePrint,
              headerButtonGroup,
              editServiceEndpoint,
              editServiceLocation,
              lookupColumns,
              masterDataObject,
              selectedTableId,
              refresh,
              pageNumber,
              fetchPageSize,
              filterColumn,
              columns,
              setPageNumber,
              setTotalRowSize,
              setCurrentLoadedRowsSize,
              setLoading,
              setGridRows,
              setMasterDataRows,
              gridRows,
              multiSelect,
              showMultiSelectButtons,
              multiSelectButtons,
              sort,
              setLookupColumns,
              tenantId,
              partnerId,
              setCustomType,
              usePagination,
              searchOverride,
              customPlaceholder,
              searchRef,
              onSearchChange,
              initialSearchValue,
              ...slotProps?.toolbar
            },
            footer: {
              //@ts-ignore
              totalRowSize,
              //@ts-ignore
              currentLoadedRowsSize,
              //@ts-ignore
              resetLayout,
              //@ts-ignore
              defaultGridState,
              //@ts-ignore
              disableLayoutReset,
              //@ts-ignore
              multiSelect,
              ...slotProps?.footer
            },
            ...slotProps
          }}
          onRowEditStart={handleRowEditStart}
          onRowEditStop={handleRowEditStop}
          getRowClassName={params => `editable-grid-theme--${params.row.hasError}`}
          sx={{
            backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1e1e1e',
            paddingRight: '5px',
            opacity: loading ? '50%' : '100%',
            '& .MuiDataGrid-row.Mui-selected': {
              ...(theme.palette.mode === 'dark'
                ? {
                    backgroundColor: theme => theme.palette.customColors.darkBg
                  }
                : {
                    backgroundColor: theme => theme.palette.customColors.primaryLightBackground
                  })
            },
            '& .Mui-checked svg': {
              color: theme => theme.palette.customColors.primaryGradient
            },
            ...sx
          }}
          rowSelectionModel={rowSelectionModel ? [rowSelectionModel] : undefined}
          {...{
            experimentalFeatures,
            processRowUpdate,
            rowModesModel,
            autoHeight,
            filterModel,
            editMode,
            density,
            selectionModel,
            onRowClick,
            disableColumnMenu,
            disableVirtualization,
            disableSelectionOnClick,
            onCellClick,
            disableRowSelectionOnClick,
            rowReordering,
            onRowOrderChange
          }}
        />
      </Box>
    </>
  );
};

export default React.memo(VigoDataGrid);
